import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import CodeBlock from '../components/CodeBlock';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I Base64 encode JSON?', a: 'Paste your JSON above and switch to Encode. The tool treats it as UTF-8 text and returns a Base64 string you can put in a header, URL or token.' },
  { q: 'Why Base64 encode JSON at all?', a: 'To move JSON safely through channels that mishandle quotes, braces or newlines — for example inside a JWT payload, an HTTP header, or a URL parameter.' },
  { q: 'Does encoding JSON to Base64 keep it valid?', a: 'Decoding returns the exact original JSON text, character for character, so it stays valid as long as your input was valid JSON.' },
  { q: 'Should I encode JSON for JWTs?', a: 'JWTs use URL-safe Base64 (no padding). Use the URL-Safe Base64 tool for token segments rather than standard Base64.' },
];

export default function JsonToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="JSON to Base64 — Encode & Decode JSON Online | EasyBase64"
        description="Encode JSON to Base64 and decode it back online for free. UTF-8 safe, instant, and fully private. Ideal for headers, tokens and URL parameters."
        path="/json-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'JSON to Base64 Converter', description: 'Encode and decode JSON as Base64 in the browser.', path: '/json-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'JSON to Base64', path: '/json-to-base64' }]),
        ]}
      />
      <PageHero
        title="JSON to Base64"
        subtitle={<>Encode JSON to Base64 and decode it back. For token segments, use <Link to="/url-safe-base64" className="underline">URL-safe Base64</Link> instead.</>}
      />
      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="standard" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Example</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed mb-4`}>
          The JSON <code>{'{"id":7}'}</code> encodes to <code>eyJpZCI6N30=</code>. In code you would do:
        </p>
        <CodeBlock
          language="JavaScript"
          code={`const json = JSON.stringify({ id: 7 });
const b64 = btoa(unescape(encodeURIComponent(json))); // "eyJpZCI6N30="
const back = JSON.parse(decodeURIComponent(escape(atob(b64))));`}
        />
      </section>
      <FaqSection title="JSON to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
